import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

import { supabase } from "@/integrations/supabase/client";

const SearchSchema = z.object({
  next: z.string().optional(),
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/auth/callback")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Signing you in · the Vibing Company®" },
      { name: "description", content: "Completing your secure sign-in to the client dashboard." },
      { property: "og:title", content: "Signing you in · the Vibing Company®" },
      { property: "og:description", content: "Completing your secure sign-in to the client dashboard." },
      { name: "robots", content: "noindex" },
    ],
  }),
  validateSearch: SearchSchema,
  component: AuthCallback,
});

function safeNext(raw: string | undefined): string {
  // Only allow same-origin path redirects to avoid open-redirect attacks.
  if (!raw) return "/dashboard";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/dashboard";
  return raw;
}

function AuthCallback() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/auth/callback" });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let done = false;
    const dest = safeNext(search.next ?? search.redirect);

    // Surface Supabase auth errors that come back via the URL fragment.
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    if (hash.includes("error=")) {
      const params = new URLSearchParams(hash.replace(/^#/, ""));
      const desc = params.get("error_description") || params.get("error") || "Sign-in link is invalid or expired.";
      setError(desc.replace(/\+/g, " "));
      return;
    }

    // Supabase auto-detects the session from the URL hash. Wait for
    // SIGNED_IN or for an existing session, then redirect.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (done) return;
      if (event === "SIGNED_IN" && session) {
        done = true;
        sub.subscription.unsubscribe();
        navigate({ to: dest, replace: true });
      }
    });

    // Fast path: session already detected on mount.
    supabase.auth.getSession().then(({ data }) => {
      if (done || !data.session) return;
      done = true;
      sub.subscription.unsubscribe();
      navigate({ to: dest, replace: true });
    });

    // Timeout fallback.
    const timeout = setTimeout(() => {
      if (done) return;
      done = true;
      sub.subscription.unsubscribe();
      setError("We couldn't sign you in. The link may have expired — request a new one.");
    }, 8000);

    return () => {
      done = true;
      clearTimeout(timeout);
      sub.subscription.unsubscribe();
    };
  }, [navigate, search.next, search.redirect]);

  if (error) {
    return (
      <div className="min-h-dvh bg-background flex items-center justify-center px-6">
        <main id="main" tabIndex={-1} className="max-w-md text-center">
          <h1 className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
            Sign-in failed
          </h1>
          <p role="alert" className="mt-4 text-foreground">{error}</p>
          <a
            href="/dashboard"
            className="mt-6 inline-block text-sm underline underline-offset-4 text-foreground/70 hover:text-foreground"
          >
            Request a new link →
          </a>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background flex items-center justify-center">
      <main id="main" tabIndex={-1}>
        <h1 className="sr-only">Signing you in</h1>
        <p role="status" className="text-muted-foreground font-mono text-xs tracking-widest uppercase">
          Signing you in…
        </p>
      </main>
    </div>
  );
}
